import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingCart, Heart, Star, ChevronLeft, Package, Shield, Truck } from 'lucide-react';
import { productsAPI } from '../utils/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [activeTab, setActiveTab] = useState('specs');

  useEffect(() => {
    productsAPI.getOne(id).then(res => { setProduct(res.data.product); setLoading(false); }).catch(() => navigate('/products'));
  }, [id, navigate]);

  const handleAddReview = async (e) => {
    e.preventDefault();
    if (!user) return toast.error('Please login to review');
    setSubmittingReview(true);
    try {
      await productsAPI.addReview(id, reviewForm);
      toast.success('Review submitted!');
      const res = await productsAPI.getOne(id);
      setProduct(res.data.product);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error submitting review');
    }
    setSubmittingReview(false);
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!product) return null;

  const images = product.images?.length ? product.images : [product.thumbnail || 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800'];

  return (
    <div className="container" style={{ padding: '40px 24px' }}>
      <button onClick={() => navigate('/products')} className="btn btn-secondary btn-sm" style={{ marginBottom: 24 }}>
        <ChevronLeft size={16} /> Back to Products
      </button>
      <div className="product-detail-layout" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, marginBottom: 60 }}>
        {/* Images */}
        <div>
          <div style={{ background: 'var(--bg-card)', borderRadius: 20, overflow: 'hidden', marginBottom: 12, border: '1px solid var(--border)', aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={images[activeImg]} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          <div className="product-detail-thumbnails" style={{ display: 'flex', gap: 8 }}>
            {images.map((img, i) => (
              <div key={i} onClick={() => setActiveImg(i)} style={{ width: 70, height: 70, borderRadius: 12, overflow: 'hidden', cursor: 'pointer', border: `2px solid ${activeImg === i ? 'var(--accent)' : 'var(--border)'}`, flexShrink: 0 }}>
                <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            ))}
          </div>
        </div>

        {/* Info */}
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <span style={{ background: 'rgba(99,102,241,0.1)', color: 'var(--accent)', padding: '4px 12px', borderRadius: 8, fontSize: 13 }}>{product.category}</span>
            {product.discount > 0 && <span className="badge badge-discount">-{product.discount}%</span>}
          </div>
          <div style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>{product.brand}</div>
          <h1 style={{ fontFamily: 'Syne', fontSize: 32, fontWeight: 800, marginBottom: 16, lineHeight: 1.2 }}>{product.name}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div className="stars" style={{ display: 'flex', gap: 3 }}>
              {[...Array(5)].map((_, i) => <span key={i} style={{ color: i < Math.round(product.rating) ? 'var(--gold)' : 'var(--text-muted)', fontSize: 18 }}>★</span>)}
            </div>
            <span style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{product.rating?.toFixed(1)} ({product.numReviews} reviews)</span>
          </div>
          <p style={{ color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 24 }}>{product.shortDescription || product.description.substring(0, 150) + '...'}</p>
          <div style={{ marginBottom: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <span className="product-detail-price" style={{ fontFamily: 'Syne', fontSize: 40, fontWeight: 800, color: 'var(--gold)' }}>${product.price.toLocaleString()}</span>
              {product.originalPrice > product.price && (
                <span style={{ fontSize: 20, color: 'var(--text-muted)', textDecoration: 'line-through' }}>${product.originalPrice.toLocaleString()}</span>
              )}
            </div>
            {product.originalPrice > product.price && (
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ background: 'rgba(34,197,94,0.12)', color: 'var(--success)', padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 700 }}>
                  Vous économisez ${(product.originalPrice - product.price).toLocaleString()} ({product.discount}%)
                </span>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
            <span style={{ color: product.stock > 0 ? 'var(--success)' : 'var(--danger)', fontSize: 14, fontWeight: 500 }}>
              {product.stock > 0 ? `✓ In Stock (${product.stock} units)` : '✗ Out of Stock'}
            </span>
            <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>SKU: {product.sku}</span>
          </div>
          {product.stock > 0 && (
            <div className="product-detail-actions" style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 0, background: 'var(--bg-secondary)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
                <button onClick={() => setQty(Math.max(1, qty-1))} style={{ padding: '12px 16px', background: 'none', border: 'none', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 18 }}>−</button>
                <span style={{ padding: '12px 16px', fontSize: 16, fontWeight: 600, minWidth: 50, textAlign: 'center' }}>{qty}</span>
                <button onClick={() => setQty(Math.min(product.stock, qty+1))} style={{ padding: '12px 16px', background: 'none', border: 'none', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 18 }}>+</button>
              </div>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => addItem(product, qty)}>
                <ShoppingCart size={18} /> Add to Cart
              </button>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {[
              [Truck, 'Free Shipping', 'Orders $500+'],
              [Shield, 'Warranty', product.warranty],
              [Package, 'Easy Returns', '30 days'],
            ].map(([Icon, title, desc]) => (
              <div key={title} style={{ padding: 14, background: 'var(--bg-secondary)', borderRadius: 12, border: '1px solid var(--border)', textAlign: 'center' }}>
                <Icon size={20} color="var(--accent)" style={{ marginBottom: 6 }} />
                <div style={{ fontSize: 12, fontWeight: 600 }}>{title}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div>
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', marginBottom: 32 }}>
          {['specs', 'description', 'reviews'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{ padding: '14px 24px', background: 'none', border: 'none', borderBottom: activeTab === tab ? '2px solid var(--accent)' : '2px solid transparent', color: activeTab === tab ? 'var(--accent)' : 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'Syne', fontWeight: 600, fontSize: 14, textTransform: 'capitalize', marginBottom: -1 }}>
              {tab === 'specs' ? 'Specifications' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {activeTab === 'specs' && product.specifications?.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
            {product.specifications.map((spec, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 20px', background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)' }}>
                <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>{spec.key}</span>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{spec.value}</span>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'description' && (
          <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, maxWidth: 720, fontSize: 16 }}>{product.description}</p>
        )}

        {activeTab === 'reviews' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 40 }}>
              <div className="card" style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: 'Syne', fontSize: 64, fontWeight: 800, color: 'var(--gold)' }}>{product.rating?.toFixed(1)}</div>
                <div className="stars" style={{ display: 'flex', justifyContent: 'center', gap: 4, marginBottom: 8 }}>
                  {[...Array(5)].map((_, i) => <span key={i} style={{ color: i < Math.round(product.rating) ? 'var(--gold)' : 'var(--text-muted)', fontSize: 22 }}>★</span>)}
                </div>
                <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>Based on {product.numReviews} reviews</div>
              </div>
              <div>
                {user && (
                  <form onSubmit={handleAddReview} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: 24, marginBottom: 24 }}>
                    <h4 style={{ fontFamily: 'Syne', marginBottom: 16 }}>Write a Review</h4>
                    <div className="form-group">
                      <label className="form-label">Rating</label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {[1,2,3,4,5].map(r => (
                          <button key={r} type="button" onClick={() => setReviewForm(p => ({ ...p, rating: r }))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 28, color: r <= reviewForm.rating ? 'var(--gold)' : 'var(--text-muted)' }}>★</button>
                        ))}
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Comment</label>
                      <textarea className="form-input" rows={3} value={reviewForm.comment} onChange={e => setReviewForm(p => ({ ...p, comment: e.target.value }))} required />
                    </div>
                    <button className="btn btn-primary" type="submit" disabled={submittingReview}>{submittingReview ? 'Submitting...' : 'Submit Review'}</button>
                  </form>
                )}
                {product.reviews?.map(rev => (
                  <div key={rev._id} style={{ padding: '16px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700 }}>{rev.name[0]}</div>
                        <span style={{ fontWeight: 600 }}>{rev.name}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 2 }}>
                        {[...Array(5)].map((_, i) => <span key={i} style={{ color: i < rev.rating ? 'var(--gold)' : 'var(--text-muted)' }}>★</span>)}
                      </div>
                    </div>
                    <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{rev.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}